import React, { createContext, useContext, useEffect, useState } from 'react'
import { useDispatch } from '../../../store'
import { canvasAuthService } from '../../../common/services/CanvasAuthService'
import { canvasSdkService } from '../services/CanvasSdkService'
import type { SalesforceContextData } from '../types/salesforce'
import { isEmpty } from 'lodash'
import { hydrateAuthorizationClaims } from '../../../common/modules/authorization'
import { getClaimsFromToken } from '../../../common/utils/helperFunctions'

const SalesforceContext = createContext<SalesforceContextData | undefined>(
  undefined
)

interface SalesforceContextProviderProps {
  children: React.ReactNode
}

export const SalesforceContextProvider: React.FC<
  SalesforceContextProviderProps
> = ({ children }) => {
  const dispatch = useDispatch()
  const [contextData, setContextData] = useState<SalesforceContextData>({
    partnerId: '',
    record: undefined,
    organization: { Id: '' },
    actions: {
      openModal: () => {},
      navigateToUrl: () => null,
      navigateToRecord: () => null,
      navigateBack: () => {},
      refreshParent: () => {},
      closeModal: () => {},
    },
    crmId: '',
    scopes: [],
    customParameters: undefined,
    isAuthInitialized: false,
    authError: null,
    loading: true, // Start with loading: true
    error: undefined,
    isInSalesforceContext: false,
  })

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await canvasAuthService.initialize()
        const partnerId = canvasAuthService.getPartnerId()
        const record = canvasAuthService.getRecord()
        const organization = canvasAuthService.getOrganization()
        const crmId = canvasAuthService.getCrmId()
        const customParameters = canvasAuthService.getCustomParameters()
        const accessToken = canvasAuthService.getAccessToken()
        const scopes = canvasAuthService.getScopes()

        const claimsFromToken = (() => {
          if (!accessToken) return { roles: [], scopes: [] }
          try {
            const claims = getClaimsFromToken(accessToken)
            return { roles: claims.roles, scopes: claims.scopes }
          } catch {
            return { roles: [], scopes: [] }
          }
        })()

        dispatch(
          hydrateAuthorizationClaims({
            source: 'canvas',
            partnerId: partnerId || null,
            roles: claimsFromToken.roles,
            scopes,
            isAuthenticated: true,
            claimsLoaded: true,
          })
        )

        setContextData({
          partnerId: partnerId || '',
          record,
          organization: organization || { Id: '' },
          actions: {
            openModal: canvasSdkService.openModal.bind(canvasSdkService),
            navigateToUrl:
              canvasSdkService.navigateToUrl.bind(canvasSdkService),
            navigateToRecord:
              canvasSdkService.navigateToRecord.bind(canvasSdkService),
            navigateBack: canvasSdkService.navigateBack.bind(canvasSdkService),
            refreshParent:
              canvasSdkService.refreshParent.bind(canvasSdkService),
            closeModal: canvasSdkService.closeModal.bind(canvasSdkService),
          },
          crmId: crmId || '',
          scopes,
          customParameters: customParameters || undefined,
          isAuthInitialized: true,
          authError: null,
          loading: false, // Loading complete
          error: undefined,
          isInSalesforceContext: true,
        })
      } catch (error) {
        setContextData(prev => ({
          ...prev,
          isAuthInitialized: false,
          authError:
            'Failed to authenticate. Please reload the page from Salesforce Canvas.',
          loading: false, // Loading complete (with error)
          error: 'salesforce_context_not_available',
          isInSalesforceContext: false,
        }))
      }
    }

    initializeAuth()
  }, [])

  useEffect(() => {
    const handler = () => {
      const accessToken = canvasAuthService.getAccessToken()
      const scopes = canvasAuthService.getScopes()

      const roles = (() => {
        if (!accessToken) return []
        try {
          const claims = getClaimsFromToken(accessToken)
          return claims.roles || []
        } catch {
          return []
        }
      })()

      dispatch(
        hydrateAuthorizationClaims({
          source: 'canvas',
          partnerId: canvasAuthService.getPartnerId(),
          roles,
          scopes,
          isAuthenticated: true,
          claimsLoaded: true,
        })
      )

      setContextData(prev => ({ ...prev, scopes }))
    }

    window.addEventListener('canvas-auth-updated', handler)
    return () => window.removeEventListener('canvas-auth-updated', handler)
  }, [dispatch])

  return (
    <SalesforceContext.Provider value={contextData}>
      {children}
    </SalesforceContext.Provider>
  )
}

export const useSalesforceContext = (): SalesforceContextData => {
  const context = useContext(SalesforceContext)

  if (context === undefined || isEmpty(context.partnerId)) {
    return {
      partnerId: '',
      record: undefined,
      organization: { Id: '' },
      actions: {
        openModal: () => {},
        navigateToUrl: () => null,
        navigateToRecord: () => null,
        navigateBack: () => {},
        refreshParent: () => {},
        closeModal: () => {},
      },
      crmId: '',
      scopes: [],
      customParameters: undefined,
      error: 'salesforce_context_not_available',
      loading: context?.loading ?? false,
      isAuthInitialized: context?.isAuthInitialized ?? false,
      authError: context?.authError ?? null,
      isInSalesforceContext: false,
    }
  }

  return context
}
